import { useRoleListQuery } from 'api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { ErrorMappingValueType, OrderType, PaginationType, SortParamsType } from 'typings';
import { Form, FormInstance, TablePaginationConfig, TableProps } from 'components';
import { RoleDataType } from './index';
import { useNotificationApi } from 'hooks/useNotificationApi';

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export interface AssignedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userName?: string | null;
  files?: any | null;
}

export const useRoles = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [drawerState, setDrawerState] = useState<boolean>(false);
  const [assignedUsersData, setAssignedUsersData] = useState<AssignedUser[] | undefined>(undefined);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [deleteItem, setDeleteItem] = useState<Record<string, any> | undefined>();

  const [filtersForm] = Form.useForm<FormInstance>();

  const navigate = useNavigate();
  const location = useLocation();
  const notificationApi = useNotificationApi();
  const notificationHandledRef = useRef(false);

  const { data: { content = [], totalElements = 0 } = {}, isLoading } = useRoleListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const { roleManagements = [] } = content?.[0] || {};
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);
  const onDeleteModalStateChange = useCallback((value: any | undefined) => {
    setDeleteItem(value);
  }, []);

  const onExpand = useCallback((users?: AssignedUser[]) => {
    setAssignedUsersData(users);
    setDrawerState((prevState) => !prevState);
  }, []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.role.view}/${value}/${ADMIN_ROUTES.role.permissions}`);
    },
    [navigate]
  );
  const onRedirectToUser = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.users.view}/${value}/account`);
    },
    [navigate]
  );
  const onAddNew = useCallback(() => {
    navigate(ADMIN_ROUTES.role.create);
  }, [navigate]);

  const handleTableChange: TableProps<RoleDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<RoleDataType> | SorterResult<RoleDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.roleId && { roleId: values.roleId }),
        ...(values.userIds?.length && { userIds: values.userIds }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastModifyDate?.[0] && {
          lastModifyDateFrom: dateFormatter(values.lastModifyDate[0], 'start'),
        }),
        ...(values.lastModifyDate?.[1] && {
          lastModifyDateTo: dateFormatter(values.lastModifyDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  useEffect(() => {
    if (location.state?.notification && !notificationHandledRef.current) {
      notificationHandledRef.current = true;
      const { type, message }: ErrorMappingValueType = location.state.notification;
      notificationApi({ type, message });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, notificationApi, navigate]);

  return {
    onOpenFilters,
    onCloseFilters,
    routeChange,
    data: roleManagements || [],
    total: totalElements || 0,
    filtersForm,
    assignedUsersData,
    handleTableChange,
    onDeleteModalStateChange,
    deleteItem,
    openFilters,
    drawerState,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
    onAddNew,
    onExpand,
    onRedirectToUser,
  };
};
